import { CalendarDays, CalendarX, Clock, MapPin, UserRound } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { isClosed, locationText, SOURCE_LABELS, statusInfo } from "@/lib/appointments";
import { appointmentMinutes, appointmentWhen, splitAppointments } from "@/lib/contact-activity";
import { contactName } from "@/lib/contacts";
import { useContactAppointments } from "@/lib/queries";
import type { AppointmentListItem, LeadCard } from "@/lib/types";
import { cn, formatDateTime, formatPhoneNumber } from "@/lib/utils";

/**
 * The contact's appointments: what is coming, soonest first, then what is done with, latest first.
 * Each says when, where it was booked, on which calendar, with whom, where, and how it stands, and
 * opens on the Calendars page, where it is changed. A rep sees the ones they host or booked.
 */
export function ContactAppointmentsTab({ contact }: { contact: LeadCard }) {
  const appointments = useContactAppointments(contact.id);

  if (appointments.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-4" />
      </div>
    );
  }
  if (appointments.error) return <ErrorState error={appointments.error} title="Could not load the appointments" />;

  const list = appointments.data;
  if (list.length === 0) {
    return (
      <EmptyState
        icon={CalendarDays}
        title="No appointments to show"
        description={`Appointments booked with ${contactName(contact) ?? "this contact"} will show up here: when, with whom, and how each one went.`}
      />
    );
  }

  const { upcoming, past } = splitAppointments(list, Date.now());
  return (
    <div className="space-y-5">
      <Group title="Upcoming" list={upcoming} phone={formatPhoneNumber(contact.phone)} empty="Nothing booked ahead." />
      <Group title="Past" list={past} phone={formatPhoneNumber(contact.phone)} empty="No past appointments." />
    </div>
  );
}

function Group({
  title, list, phone, empty,
}: {
  title: string;
  list: AppointmentListItem[];
  phone: string;
  empty: string;
}) {
  const id = React.useId();
  return (
    <section aria-labelledby={id} className="space-y-2">
      <h3 id={id} className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
        {title} <span className="font-normal tabular-nums">({list.length})</span>
      </h3>
      {list.length === 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-text-muted">
          <CalendarX className="size-3.5" aria-hidden />
          {empty}
        </p>
      ) : (
        <ol className="space-y-2">
          {list.map((a) => (
            <li key={a.id}>
              <AppointmentCard appointment={a} phone={phone} />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function isLink(location: string): boolean {
  return /^https?:\/\//i.test(location);
}

function AppointmentCard({ appointment: a, phone }: { appointment: AppointmentListItem; phone: string }) {
  const status = statusInfo(a.status);
  const start = new Date(a.starts_at);
  const off = isClosed(a.status);
  const location = locationText(a.location_kind, a.location_detail, phone);
  return (
    <article className={cn("flex gap-3 rounded-lg border border-border p-3", off && "opacity-70")}>
      <div className="flex w-12 shrink-0 flex-col items-center rounded-md bg-surface-sunken py-1.5 text-center" aria-hidden>
        <span className="text-2xs font-medium uppercase text-text-muted">
          {start.toLocaleDateString(undefined, { month: "short" })}
        </span>
        <span className="text-lg font-semibold leading-tight text-text">{start.getDate()}</span>
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className={cn("text-sm font-semibold text-text", off && "line-through")}>{a.title}</h4>
          <Badge tone="outline" size="sm">{SOURCE_LABELS[a.source] ?? a.source}</Badge>
          <Badge tone={status.tone} size="sm" className="ml-auto">{status.label}</Badge>
        </div>
        <dl className="grid gap-x-4 gap-y-1 text-xs text-text-muted sm:grid-cols-2">
          <Fact icon={Clock} label="When">
            {appointmentWhen(a)} <span className="text-text-subtle">({appointmentMinutes(a)} min)</span>
          </Fact>
          <Fact icon={CalendarDays} label="Calendar">{a.calendar_name || "None"}</Fact>
          <Fact icon={UserRound} label="Assigned to">{a.owner_name ?? "Unassigned"}</Fact>
          <Fact icon={MapPin} label="Location">
            {isLink(location) ? (
              <a href={location} target="_blank" rel="noreferrer" className="break-all text-accent-11 hover:underline">
                {location}
              </a>
            ) : (
              location
            )}
          </Fact>
        </dl>
        {a.notes && <p className="whitespace-pre-wrap rounded-md bg-surface-sunken px-2.5 py-1.5 text-xs text-text">{a.notes}</p>}
        <p className="text-2xs text-text-subtle">
          Booked on {formatDateTime(a.created_at)}
          {" · "}
          <Link to={`/calendars?tab=list&appointment=${a.id}`} className="font-medium text-accent-11 hover:underline">
            Open in Calendars
          </Link>
        </p>
      </div>
    </article>
  );
}

function Fact({ icon: Icon, label, children }: { icon: typeof Clock; label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-start gap-1.5">
      <Icon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <dt className="sr-only">{label}</dt>
      <dd className="min-w-0 text-text">{children}</dd>
    </div>
  );
}
